import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MapPin, Maximize, BedDouble, ArrowRight, Heart, Send, Sparkles, Image as ImageIcon, Play, Rocket } from "lucide-react";
import { fmtPrice, API } from "./api";
import { ShareButtons } from "./ShareButtons";
import { parseVideo } from "./MediaBackground";
import { isFavorite, toggleFavorite } from "./analytics";
import { useAuth } from "./authApi";

function videoEmbedSrc(url) {
  const v = parseVideo(url);
  if (!v) return null;
  if (v.type === "youtube") return { type: "iframe", src: `https://www.youtube.com/embed/${v.id}?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&loop=1&playlist=${v.id}&modestbranding=1&showinfo=0` };
  if (v.type === "vimeo") return { type: "iframe", src: `https://player.vimeo.com/video/${v.id}?autoplay=1&muted=1&loop=1&background=0` };
  if (v.type === "file") return { type: "file", src: v.url };
  return null;
}

const modeBtn = (active) => `flex-1 min-w-0 inline-flex items-center justify-center gap-1 text-[13px] uppercase tracking-tight font-semibold px-1.5 py-1.5 rounded-full border transition-colors whitespace-nowrap ${active ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "border-zinc-300 text-zinc-600 hover:border-[#C19A6B]/60 hover:text-[#C19A6B]"}`;

const STATUS_COLORS = {
  "Disponible": "bg-white/15 border-white/25 text-white",
  "Sous compromis": "bg-amber-500/25 border-amber-200/40 text-white",
  "Vendu": "bg-red-500/25 border-red-200/40 text-white",
};

export const ListingCard = ({ listing, index = 0, privateView = false, recency = false }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  // Traduction du titre/résumé de la carte (IA, en cache ; fallback original).
  const [loc, setLoc] = useState({});
  useEffect(() => {
    const lang = i18n.language || "fr";
    if (lang === "fr" || !listing.slug) { setLoc({}); return; }
    let active = true;
    fetch(`${API}/i18n/listing/${listing.slug}?lang=${encodeURIComponent(lang)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (active && d && d.fields) setLoc(d.fields); })
      .catch(() => {});
    return () => { active = false; };
  }, [listing.slug, i18n.language]);
  const tTitle = loc.title || listing.title;
  const tSummary = loc.summary || listing.summary;
  const recentDays = (recency && listing.created_at) ? Math.floor((Date.now() - new Date(listing.created_at).getTime()) / 86400000) : null;
  const isRecent = recentDays != null && recentDays <= 14;
  const statusLabel = { "Disponible": t("listings.opt_available"), "Sous compromis": t("listings.opt_under_offer"), "Vendu": t("listings.opt_sold") }[listing.status] || listing.status;
  const [fav, setFav] = useState(() => isFavorite(listing.slug));
  const onFav = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const now = await toggleFavorite(listing.slug);
    setFav(now);
    if (now && !user) window.dispatchEvent(new CustomEvent("omk-need-account"));
  };
  const onOffer = (e) => {
    e.preventDefault(); e.stopPropagation();
    nav(`/bien/${listing.slug}/offre`);
  };
  // Vraies photos du bien (hors projections IA / home staging)
  const photos = React.useMemo(() => {
    const arr = (listing.images && listing.images.length ? listing.images : [listing.cover]).filter(Boolean);
    return arr.length ? arr : [listing.cover].filter(Boolean);
  }, [listing]);
  // Projections IA (home staging) — champ 'staging', image 'after'
  const aiPhotos = React.useMemo(() => (listing.staging || []).map((s) => s.after).filter(Boolean), [listing]);
  const videoEmbed = React.useMemo(() => videoEmbedSrc(listing.video_url), [listing]);
  const [mode, setMode] = useState("photos"); // photos | ai | video
  const [photoIdx, setPhotoIdx] = useState(0);
  const timerRef = useRef(null);
  const activeSet = (mode === "ai" && aiPhotos.length ? aiPhotos : photos);
  const startCycle = () => {
    if (mode === "video") return;
    if (activeSet.length < 2) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setPhotoIdx((i) => (i + 1) % activeSet.length), 1400);
  };
  const stopCycle = () => { clearInterval(timerRef.current); setPhotoIdx(0); };
  const pickMode = (m) => (e) => { e.preventDefault(); e.stopPropagation(); clearInterval(timerRef.current); setPhotoIdx(0); setMode(m); };
  const onImgClick = (e) => {
    if (mode === "video") { e.preventDefault(); e.stopPropagation(); return; }
    if (mode === "ai" && aiPhotos.length) { e.preventDefault(); e.stopPropagation(); setPhotoIdx((i) => (i + 1) % aiPhotos.length); }
  };
  useEffect(() => () => clearInterval(timerRef.current), []);
  const hasExtra = aiPhotos.length > 0 || !!videoEmbed;
  const owner = listing.owner || null;
  const ownerName = owner ? ((`${owner.first_name || ""} ${owner.last_name || ""}`.trim()) || owner.display_name || owner.name || owner.company) : "";
  const ownerPhoto = owner ? (owner.photo || owner.logo || "") : "";
  return (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
    whileHover={{ scale: 1.05, y: -8, zIndex: 30, boxShadow: "0 35px 70px -22px rgba(0,0,0,0.45)" }}
    onClick={() => nav(`/bien/${listing.slug}`)}
    role="link"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === "Enter") nav(`/bien/${listing.slug}`); }}
    data-testid={`listing-card-${listing.slug}`}
    className="group relative cursor-pointer bg-white border border-zinc-200 rounded-[18px] overflow-hidden flex flex-col shadow-lg shadow-black/10 hover:border-[#C19A6B]/60 transition-colors"
  >
    <Link to={`/bien/${listing.slug}`} onMouseEnter={startCycle} onMouseLeave={stopCycle} onClick={onImgClick} className="relative aspect-[16/11] overflow-hidden block">
      {mode === "video" && videoEmbed ? (
        videoEmbed.type === "file" ? (
          <video src={videoEmbed.src} data-testid={`card-video-${listing.slug}`} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline controls />
        ) : (
          <iframe src={videoEmbed.src} title={listing.title} data-testid={`card-video-${listing.slug}`} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] aspect-[16/9] pointer-events-none border-0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
        )
      ) : (
        <img src={activeSet[photoIdx] || listing.cover} alt={listing.title} loading="lazy" className="w-full h-full object-cover transition-opacity duration-500" />
      )}
      {mode === "ai" && (
        <span className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-2.5 py-0.5 rounded-full bg-[#C19A6B] text-white text-[9px] uppercase tracking-wider font-semibold inline-flex items-center gap-1" data-testid={`ai-badge-${listing.slug}`}>
          <Sparkles className="w-2.5 h-2.5" /> {t("ai_staging")}
        </span>
      )}
      {mode !== "video" && activeSet.length > 1 && (
        <div className={`absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1 transition-opacity duration-300 z-10 ${mode === "ai" ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} data-testid={`card-photo-dots-${listing.slug}`}>
          {activeSet.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === photoIdx ? "w-4 bg-[#C19A6B]" : "w-1.5 bg-white/60"}`} />
          ))}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
      <div className="absolute top-3 left-3 flex items-center gap-1.5">
        {isRecent && (
          <span data-testid={`recent-badge-${listing.slug}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-white text-[#050505] shadow-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C19A6B]" />
            {recentDays <= 3 ? t("recent_badge_new") : t("recent_badge_since", { count: Math.max(1, recentDays) })}
          </span>
        )}
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium border backdrop-blur-md ${STATUS_COLORS[listing.status] || "bg-white/15 border-white/25 text-white"}`}>{statusLabel}</span>
        <span className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium bg-[#C19A6B] text-white">{listing.collection}</span>
        {listing.boost && listing.boost.expires_at && new Date(listing.boost.expires_at) > new Date() && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[#C19A6B] text-white shadow-lg" data-testid={`boost-badge-${listing.slug}`}><Rocket className="w-3 h-3" /> Boosté</span>
        )}
      </div>
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <button onClick={onOffer} data-testid={`offer-btn-${listing.slug}`} aria-label={t("listings.offer")} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/80 hover:bg-black border border-white/15 hover:border-[#C19A6B]/60 text-[#C19A6B] text-[10px] uppercase tracking-wider font-medium backdrop-blur-md transition-colors">
          <Send className="w-3 h-3" /> {t("listings.offer")}
        </button>
        <ShareButtons slug={listing.slug} title={tTitle} price={fmtPrice(listing.price)} city={listing.city} minimal />
        <button onClick={onFav} data-testid={`fav-btn-${listing.slug}`} aria-label={fav ? "Retirer des favoris" : "Ajouter aux favoris"} className={`inline-flex items-center justify-center px-1.5 py-1 rounded-full backdrop-blur-md border transition-colors ${fav ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "bg-black/45 border-white/15 text-white hover:text-[#C19A6B]"}`}>
          <Heart className={`w-3.5 h-3.5 ${fav ? "fill-black" : ""}`} />
        </button>
      </div>
      <span className="absolute bottom-3 left-4 text-xl font-bold text-white [text-shadow:_0_2px_12px_rgba(0,0,0,0.95),_0_1px_3px_rgba(0,0,0,0.9)]">{fmtPrice(listing.price)}</span>
      {listing.owner && (
        <span data-testid={`owner-badge-${listing.slug}`} className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm border border-white/10 text-[10px] uppercase tracking-wider text-[#C19A6B]">
          {listing.owner.logo
            ? <img src={listing.owner.logo} alt={listing.owner.company || listing.owner.name} className="h-4 w-auto max-w-[80px] object-contain" />
            : (listing.owner.company || listing.owner.name)}
        </span>
      )}
    </Link>
    {hasExtra && (
      <div className="flex flex-nowrap items-stretch gap-1 px-3 pt-3" data-testid={`media-modes-${listing.slug}`}>
        <button onClick={pickMode("photos")} onMouseEnter={pickMode("photos")} data-testid={`mode-photos-${listing.slug}`} className={modeBtn(mode === "photos") + " whitespace-nowrap"}><ImageIcon className="w-3 h-3 shrink-0" /> {t("real_photos")}</button>
        {aiPhotos.length > 0 && <button onClick={pickMode("ai")} onMouseEnter={pickMode("ai")} data-testid={`mode-ai-${listing.slug}`} className={modeBtn(mode === "ai") + " whitespace-nowrap" + (mode === "ai" ? "" : " !bg-[#F1F3F5] !text-[#A88358] !border-[#E5E7EB]")}><Sparkles className="w-3 h-3 shrink-0" /> {t("listings.ai_staging_short")}</button>}
        {videoEmbed && <button onClick={pickMode("video")} onMouseEnter={pickMode("video")} data-testid={`mode-video-${listing.slug}`} className={modeBtn(mode === "video") + " whitespace-nowrap"}><Play className="w-3 h-3 shrink-0" /> {t("video")}</button>}
      </div>
    )}
    <div className="p-5 flex flex-col flex-1 space-y-3">
      <h3 className="text-base font-medium leading-tight line-clamp-2 overflow-hidden">
        {(() => {
          const words = (tTitle || "").trim().split(/\s+/);
          const mid = Math.ceil(words.length / 2);
          const l1 = words.slice(0, mid).join(" ");
          const l2 = words.slice(mid).join(" ");
          return <><span className="text-[#050505]">{l1}</span>{l2 && <><br /><span className="text-[#C19A6B] font-semibold italic">{l2}</span></>}</>;
        })()}
      </h3>
      <div className="flex items-center gap-1.5 text-xs text-zinc-600"><MapPin className="w-3.5 h-3.5 text-[#C19A6B]" /> {listing.city} · {listing.department}</div>
      <div className="flex gap-4 text-[11px] font-mono text-zinc-500">
        <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5" /> {listing.surface} m²</span>
        <span>{listing.rooms} {t("listings.rooms_word")}</span>
        <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {listing.bedrooms} {t("listings.bedrooms_word")}</span>
      </div>
      <p className="text-xs font-light text-zinc-600 leading-relaxed flex-1 line-clamp-2">{tSummary}</p>
      <div className="flex flex-wrap gap-1.5">
        {(listing.highlights || []).slice(0, 2).map((h) => (
          <span key={h} className="text-[10px] px-2 py-1 bg-black/5 rounded-full text-zinc-600">{h}</span>
        ))}
      </div>
      {owner ? (
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-200">
          <Link
            to={`/${owner.slug}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`card-advisor-${listing.slug}`}
            className="flex items-center gap-2.5 min-w-0 group/adv"
          >
            {ownerPhoto ? (
              <img src={ownerPhoto} alt={ownerName} className="w-9 h-9 rounded-full object-cover border-2 border-[#C19A6B]/70 shrink-0 shadow-md" />
            ) : (
              <span className="w-9 h-9 rounded-full bg-[#C19A6B]/20 border-2 border-[#C19A6B]/70 flex items-center justify-center text-[#C19A6B] text-sm font-semibold shrink-0">{(ownerName || "?").charAt(0)}</span>
            )}
            <div className="leading-tight min-w-0">
              <p className="calli text-sm text-[#C19A6B] leading-none">{t("your_advisor")}</p>
              <p className="text-xs font-medium text-[#050505] truncate group-hover/adv:text-[#C19A6B] transition-colors">{ownerName}</p>
            </div>
          </Link>
          <Link
            to={`/bien/${listing.slug}`}
            data-testid={`discover-${listing.slug}`}
            className="shrink-0 inline-flex flex-col items-end leading-tight text-[10px] font-medium uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84] transition-colors text-right"
          >
            <span className="inline-flex items-center gap-1">{t("listings.see")} <ArrowRight className="w-3 h-3" /></span>
            <span>{privateView ? t("listings.the_private_listing") : t("listings.the_listing")}</span>
          </Link>
        </div>
      ) : (
        <div className="pt-1 flex justify-end">
          <Link to={`/bien/${listing.slug}`} data-testid={`discover-${listing.slug}`} className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#C19A6B] hover:gap-2.5 transition-all">
            {t("listings.see")} {privateView ? t("listings.the_private_listing") : t("listings.the_listing")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </div>
  </motion.div>
  );
};
